/**
 * Screen-scoped design tokens for the **sending overlay** shown while a sign
 * request is being dispatched, and the **finishing overlay** shown while a
 * signer's signature is being applied. Both are the same card — only the
 * animation and the copy differ, so they share one component and one token set.
 *
 * Mirrors the "Sending Overlay" block of `Email Configurations.dc.html` and the
 * "Finishing Overlay" block of `Signer Experience.dc.html` 1:1 —
 * every colour, size, radius, spacing and type value the overlay needs lives
 * here so the component never inlines a literal. Colours that already exist in
 * the shared `signer` palette are referenced from it rather than re-declared;
 * only the envelope/paper fills the palette does not carry yet are local. Once
 * every screen is re-skinned these get promoted into `src/theme` and this file
 * is deleted (see UI_IMPLEMENTATION_RULES.md § Temporary Screen Tokens).
 */

import { alpha } from "@mui/material/styles";

import { signer, white as sharedWhite } from "theme/colors";

/** Fills the shared palette does not carry yet. */
const palette = {
  white: sharedWhite,
  /** Body copy under the title — one step lighter than `signer.text.meta`. */
  desc: signer.text.subtitle,
  /** Footer strip fill. */
  footerBg: signer.surface.subtle,
  /** Placeholder text lines on the animated document. */
  docLine: "#EDEAF3",
  /** Envelope body — light peach front, slightly deeper pocket. */
  envelopeTop: signer.orange.tint,
  envelopeBottom: "#FBE3D4",
  envelopePocketBottom: "#F8D6C0",
} as const;

export const sendingLoaderTokens = {
  palette,

  /** Full-screen scrim behind the card. */
  overlay: {
    zIndex: 2000,
    bg: alpha(signer.ink, 0.4),
    blur: "blur(7px)",
    padding: "24px",
  },

  /** The white dialog surface. */
  card: {
    width: 480,
    radius: "18px",
    bg: palette.white,
    shadow: `0 24px 70px ${alpha("#000000", 0.4)}`,
  },

  /** Indeterminate progress strip along the top edge of the card. */
  progress: {
    height: "4px",
    trackBg: signer.border.light,
    barWidth: "40%",
    barRadius: "4px",
    barGradient: `linear-gradient(90deg, ${signer.purple.main}, ${signer.gradient.coral}, ${signer.gradient.amber})`,
    duration: "1.3s",
    easing: "cubic-bezier(.45,.1,.55,.9)",
  },

  /** Padded content region: animation, title, description. */
  body: {
    padding: "38px 44px 36px",
  },

  /** The document-slides-into-a-sealed-envelope animation. */
  envelope: {
    stageWidth: 150,
    stageHeight: 88,
    /** One full loop; every layer shares it so the beats stay in sync. */
    duration: "3.2s",

    doc: {
      width: 64,
      height: 74,
      bottom: 26,
      radius: "6px",
      padding: "10px 9px",
      bg: palette.white,
      border: `1.5px solid ${signer.border.main}`,
      lineHeight: "4px",
      lineRadius: "2px",
      lineGap: "6px",
      lineStrongColor: signer.disabled,
      lineColor: palette.docLine,
      signatureGap: "10px",
      signatureHeight: "9px",
      signatureWidth: "46%",
      signatureBorder: `2px solid ${signer.orange.main}`,
    },

    front: {
      width: 112,
      height: 52,
      radius: "7px",
      border: `1.5px solid ${signer.orange.border}`,
      bg: `linear-gradient(180deg, ${palette.envelopeTop}, ${palette.envelopeBottom})`,
      pocketBg: `linear-gradient(180deg, ${palette.envelopeBottom}, ${palette.envelopePocketBottom})`,
      pocketClip: "polygon(0 0, 50% 62%, 100% 0, 100% 100%, 0 100%)",
    },

    flap: {
      height: 34,
      radius: "6px",
      clip: "polygon(2% 100%, 50% 6%, 98% 100%)",
      bg: `linear-gradient(180deg, ${signer.orange.main}, ${signer.orange.dark})`,
    },

    seal: {
      size: 30,
      top: 16,
      bg: `linear-gradient(135deg, ${signer.purple.main}, ${signer.gradient.coral})`,
      shadow: `0 4px 12px ${alpha(signer.purple.main, 0.45)}`,
      iconSize: 14,
      iconColor: palette.white,
      iconStrokeWidth: 2.6,
    },
  },

  /** The signature-being-drawn animation, used while a signer finishes. */
  signature: {
    stageWidth: 150,
    stageHeight: 80,
    svgWidth: 150,
    svgHeight: 84,
    viewBox: "0 0 150 84",
    duration: "2.6s",
    /** Hand-drawn signature flourish; the dash offset animates along it. */
    strokePath:
      "M14 58 C 30 20, 44 20, 46 44 C 47 62, 34 66, 32 54 C 30 42, 52 30, 66 36 C 78 41, 72 58, 84 54 C 94 50, 96 34, 108 38 C 120 42, 122 54, 136 44",
    strokeColor: signer.orange.main,
    strokeWidth: 3.5,
    /** Must match the path length the `drawSig` keyframe sweeps through. */
    dashArray: 420,
    /** The rule the signature sits on. */
    baselinePath: "M14 70 H 136",
    baselineColor: palette.docLine,
    baselineWidth: 2,
    /** Class the stage uses to hang the keyframe on the animated path. */
    pathClass: "sendingLoaderSigPath",
  },

  /** "Sending your request" + the three pulsing dots. */
  title: {
    marginTop: "20px",
    gap: "3px",
    fontSize: "19px",
    fontWeight: 800,
    color: signer.ink,
    letterSpacing: "-.01em",
    dotColor: signer.orange.main,
    dotDuration: "1.2s",
    /** Stagger between the three dots. */
    dotDelays: ["0s", ".2s", ".4s"] as const,
  },

  description: {
    marginTop: "9px",
    fontSize: "13px",
    lineHeight: 1.6,
    color: palette.desc,
    /** The two designs wrap their copy at slightly different widths. */
    maxWidth: {
      envelope: "340px",
      signature: "320px",
    },
  },

  /** Reassurance strip pinned to the bottom of the card. */
  footer: {
    padding: "18px 24px",
    gap: "8px",
    bg: palette.footerBg,
    borderTop: `1px solid ${signer.border.main}`,
    iconSize: 14,
    iconColor: signer.purple.main,
    iconStrokeWidth: 2,
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: 1.4,
    color: signer.text.meta,
  },
} as const;
